import type { UserRole } from '@rendimees24/shared-types';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import i18n from 'i18next';

import { resolveLocaleOnSignIn } from '../lib/accountLocale';
import { getFirebase } from '../services/firebase';
import { stampLastSignInIfStale } from '../services/internalUsers';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: UserRole | null;
  /** Client IDs this user has access to (only meaningful for `client_user`). */
  permissions: string[];
  /**
   * An anonymous Firebase session — the B2C guest checkout on `/order`. It is
   * NOT a signed-in account for routing; ask `isSignedIn` (./signedIn).
   */
  isAnonymous: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /**
   * Re-fetch the /users/{uid} doc and update context. Call this after
   * server-side mutations that change the caller's role or permissions
   * (e.g. self-signup, accepting an invite).
   */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  refresh: async () => {
    /* noop default */
  },
});

async function loadProfile(uid: string): Promise<{
  role: UserRole | null;
  permissions: string[];
  lastSignInAt: string | null;
  preferredLanguage: string | null;
  exists: boolean;
}> {
  const { firestore } = getFirebase();
  let role: UserRole | null = null;
  let permissions: string[] = [];
  let lastSignInAt: string | null = null;
  let preferredLanguage: string | null = null;
  let exists = false;
  try {
    const snap = await getDoc(doc(firestore, 'users', uid));
    exists = snap.exists();
    const data = snap.data();
    role = (data?.role as UserRole | undefined) ?? null;
    permissions = (data?.permissions as string[] | undefined) ?? [];
    // The agreed app + notification language. Absent means "not agreed yet",
    // and the app stays where it is rather than inventing one.
    preferredLanguage =
      (data?.profile as { preferredLanguage?: string } | undefined)?.preferredLanguage ?? null;
    // serverTimestamp persists as Firestore Timestamp; toDate() if so.
    const raw = data?.lastSignInAt;
    if (raw && typeof (raw as { toDate?: () => Date }).toDate === 'function') {
      lastSignInAt = (raw as { toDate: () => Date }).toDate().toISOString();
    } else if (typeof raw === 'string') {
      lastSignInAt = raw;
    }
  } catch {
    // Firestore access denied — no /users doc yet. Leave role null; bootstrap below will fix.
  }
  return { role, permissions, lastSignInAt, preferredLanguage, exists };
}

/**
 * Server-side bootstrap of /users/{uid} for brand-new sign-ins. Idempotent:
 * the callable no-ops if the doc already exists. Falls through silently on
 * any error so a transient deploy issue can't lock a user out — they'll
 * still see the role-null fallback in the route guards.
 */
async function selfBootstrap(): Promise<
  { ok: boolean; created: boolean; claimUpdated?: boolean } | undefined
> {
  try {
    const { functions } = getFirebase();
    const callable = httpsCallable<
      unknown,
      { ok: boolean; created: boolean; claimUpdated?: boolean }
    >(functions, 'selfBootstrapUser');
    const r = await callable({});
    return r.data;
  } catch {
    /* swallow — non-fatal */
    return undefined;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { auth } = getFirebase();
    // Firebase ends a session by itself when the refresh token is revoked —
    // which is exactly what happens when an admin removes somebody from a
    // company while they are working. That landed them on a bare login form
    // with no explanation, indistinguishable from an idle timeout.
    //
    // A marker in localStorage rather than a variable in this closure: the
    // revocation is discovered on the next LOAD, and on a fresh load this
    // callback fires with `null` before it could have seen anybody. What is
    // true across loads is "this browser was signed in, and now it is not,
    // and nobody pressed Log out" — `signOut` clears the marker itself.
    const unsub = auth.onAuthStateChanged(async (fbUser) => {
      try {
        if (!fbUser) {
          setUser(null);
          try {
            if (localStorage.getItem('rm24.wasSignedIn') === '1') {
              localStorage.removeItem('rm24.wasSignedIn');
              sessionStorage.setItem('rm24.sessionEnded', '1');
            }
          } catch {
            // Storage blocked: no explanation, but nothing breaks either.
          }
          return;
        }
        // Only a real account leaves the marker: a guest's anonymous session
        // ending is not "your session was ended" for anybody.
        if (!fbUser.isAnonymous) {
          try {
            localStorage.setItem('rm24.wasSignedIn', '1');
          } catch {
            // As above.
          }
        }
        let profile = await loadProfile(fbUser.uid);

        // Always run bootstrap: it creates the /users doc for first-ever
        // sign-ins (role=client_user → client portal) AND mirrors the role into
        // the auth token as a custom claim, which Cloud Storage rules rely on.
        const boot = await selfBootstrap();
        if (!profile.exists || !profile.role) {
          profile = await loadProfile(fbUser.uid);
        }
        // If the role claim was just (re)set, the current token predates it —
        // force a refresh so role-gated Storage uploads work this session.
        if (boot?.claimUpdated) {
          await fbUser.getIdToken(true);
        }
        const { role, permissions, lastSignInAt } = profile;

        // Open in the language agreed with this person. The account is the
        // authority; a choice they made on this device wins over it, and a
        // previous user of a shared phone never does. An account with nothing
        // agreed opens in the app default; an anonymous guest (/order) keeps
        // the language they chose — see lib/accountLocale.
        const nextLocale = resolveLocaleOnSignIn({
          uid: fbUser.uid,
          accountLocale: profile.preferredLanguage,
          current: i18n.language,
          isAnonymous: fbUser.isAnonymous,
        });
        // Deliberately NOT cached: caching an account-derived language would
        // make it outrank the account on the next sign-in, so a later
        // correction by the admin would never reach this device. Only the
        // switcher records a choice.
        if (nextLocale) void i18n.changeLanguage(nextLocale);

        setUser({
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName,
          role,
          permissions,
          isAnonymous: fbUser.isAnonymous,
        });
        // Stamp lastSignInAt on auth state change. Rate-limited to once
        // per hour so a page refresh doesn't burn a write. Fire-and-forget
        // — failure is non-fatal for the session.
        void stampLastSignInIfStale(fbUser.uid, lastSignInAt).catch(() => {
          /* swallow — non-fatal */
        });
      } catch (err) {
        // Profile load / self-bootstrap failed (rules, network, callable
        // timeout). Don't leave the app stuck on the spinner — drop to an
        // unauthenticated state so the user can re-attempt. The window
        // error logger (errorLog.ts) catches this for triage.
        console.error('[auth] bootstrap failed', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  const refresh = useCallback(async () => {
    const { auth } = getFirebase();
    const fbUser = auth.currentUser;
    if (!fbUser) return;
    const { role, permissions } = await loadProfile(fbUser.uid);
    setUser({
      uid: fbUser.uid,
      email: fbUser.email,
      displayName: fbUser.displayName,
      role,
      permissions,
      isAnonymous: fbUser.isAnonymous,
    });
  }, []);

  const value = useMemo(() => ({ user, loading, refresh }), [user, loading, refresh]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
